import React, { ComponentProps, useMemo } from 'react';

import { useUsers, useRoles } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import Tooltip from '@adsk/alloy-react-tooltip';

import { MentionData } from '../../consts';
import { useTranslation } from '../../i18n';
import ShowMore from '../ShowMore';

type MentionRoleTooltipProps = Omit<
  ComponentProps<typeof Tooltip>,
  'content'
> & {
  mention: MentionData;
};

export const MentionRoleTooltip = ({
  mention,
  children,
  ...props
}: MentionRoleTooltipProps) => {
  const { t } = useTranslation();
  const roles = useRoles();
  const users = useUsers();
  const currentRole = useMemo(
    () => roles.data.find((role) => role.memberGroupId === mention.id),
    [mention.id, roles.data],
  );

  return (
    <Tooltip
      {...props}
      content={
        <Flex
          data-testid={`MentionRoleTooltip_Content_${mention?.id}`}
          alignItems="center"
          style={{ wordBreak: 'break-all' }}
        >
          <span>
            <Box typography="labelMedium" color="charcoal900">
              {currentRole?.name || mention?.name || t('FORMER_ROLE')}
            </Box>
            <Box typography="labelMedium" color="charcoal700">
              <ShowMore
                list={users.data
                  ?.filter((user) =>
                    user.roleIds?.includes(currentRole?.id || ''),
                  )
                  .map((item) => item.name)}
              />
            </Box>
          </span>
        </Flex>
      }
      triggerTarget={Tooltip.TRIGGER_TARGETS.TOOLTIP}
      variant={Tooltip.VARIANTS.LIGHT}
    >
      <span data-testid={`MentionRoleTooltip_Trigger_${mention?.id}`}>
        @{children || currentRole?.name || mention?.name || t('FORMER_ROLE')}
      </span>
    </Tooltip>
  );
};
